import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AFTER_SIGNED_SCAN_TIMELINE_OPTIONS,
  START_BEFORE_SIGNED_SCAN_WARNING,
  formatAfterSignedScanTimestamp,
  isWindowStartBeforeSignedScan,
  type AfterSignedScanTimelineChange,
  type AfterSignedScanTimelineMode,
} from '../../domain/incidents/afterSignedScanReview';
import { formatSiteDateTimeLocalInput, parseSiteDateTimeLocalInput } from '../../domain/siteTime/siteTime';

export type AfterSignedScanTimelineSelectorProps = {
  signedScanAt: string;
  timelineMode: AfterSignedScanTimelineMode;
  windowStartIso: string;
  windowEndIso?: string | null;
  onTimelineChange: (change: AfterSignedScanTimelineChange) => void;
  onResolveStartBeforeSignedScan: (action: 'full_history' | 'reset_start') => void;
  siteTimeZone?: string | null;
};

export const AfterSignedScanTimelineSelector: React.FC<AfterSignedScanTimelineSelectorProps> = ({
  signedScanAt,
  timelineMode,
  windowStartIso,
  windowEndIso,
  onTimelineChange,
  onResolveStartBeforeSignedScan,
  siteTimeZone = null,
}) => {
  const selectedOption = timelineMode === 'full_case_history'
    ? 'full_case_history'
    : timelineMode;
  const startBeforeSignedScan = isWindowStartBeforeSignedScan(windowStartIso, signedScanAt);
  const showEndPicker = selectedOption === 'since_scan_custom_end'
    || selectedOption === 'custom_new_activity';
  const showStartPicker = selectedOption === 'custom_new_activity';
  const endInputValue = useMemo(
    () => (windowEndIso ? formatSiteDateTimeLocalInput(windowEndIso, siteTimeZone) : ''),
    [siteTimeZone, windowEndIso],
  );
  const startInputValue = useMemo(
    () => formatSiteDateTimeLocalInput(windowStartIso || signedScanAt, siteTimeZone),
    [signedScanAt, siteTimeZone, windowStartIso],
  );

  const handleOptionChange = (nextOption: AfterSignedScanTimelineMode) => {
    if (nextOption === 'full_case_history') {
      onTimelineChange({ option: 'full_case_history' });
      return;
    }
    if (nextOption === 'since_latest_scan') {
      onTimelineChange({ option: 'since_latest_scan' });
      return;
    }
    if (nextOption === 'since_scan_custom_end') {
      onTimelineChange({
        option: 'since_scan_custom_end',
        scanScopeEnd: windowEndIso || undefined,
      });
      return;
    }
    onTimelineChange({
      option: 'custom_new_activity',
      scanScopeStart: windowStartIso || signedScanAt,
      scanScopeEnd: windowEndIso || undefined,
    });
  };

  const handleEndChange = (localValue: string) => {
    const endIso = parseSiteDateTimeLocalInput(localValue, siteTimeZone);
    if (selectedOption === 'since_scan_custom_end') {
      onTimelineChange({
        option: 'since_scan_custom_end',
        scanScopeEnd: endIso,
      });
      return;
    }
    if (selectedOption === 'custom_new_activity') {
      onTimelineChange({
        option: 'custom_new_activity',
        scanScopeStart: windowStartIso || signedScanAt,
        scanScopeEnd: endIso,
      });
    }
  };

  const handleStartChange = (localValue: string) => {
    const startIso = parseSiteDateTimeLocalInput(localValue, siteTimeZone);
    onTimelineChange({
      option: 'custom_new_activity',
      scanScopeStart: startIso,
      scanScopeEnd: windowEndIso || undefined,
    });
  };

  return (
    <Stack spacing={1.25} data-testid="after-signed-scan-timeline-selector">
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Timeline
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose what new activity to review after the latest Signed Safety Scan.
        </Typography>
      </Box>

      <FormControl size="small" fullWidth>
        <InputLabel id="after-signed-scan-timeline-label">New activity window</InputLabel>
        <Select
          labelId="after-signed-scan-timeline-label"
          label="New activity window"
          value={selectedOption}
          onChange={(event) => handleOptionChange(event.target.value as AfterSignedScanTimelineMode)}
        >
          {AFTER_SIGNED_SCAN_TIMELINE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedOption !== 'full_case_history' ? (
        <Grid container spacing={1.5}>
          {showStartPicker ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="From"
                type="datetime-local"
                value={startInputValue}
                onChange={(event) => handleStartChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'after-signed-scan-custom-start' }}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="From"
                value={formatAfterSignedScanTimestamp(signedScanAt, siteTimeZone)}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          {showEndPicker ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="To"
                type="datetime-local"
                value={endInputValue}
                onChange={(event) => handleEndChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'after-signed-scan-custom-end' }}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="To"
                value={windowEndIso ? formatAfterSignedScanTimestamp(windowEndIso, siteTimeZone) : 'now'}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>
      ) : null}

      {startBeforeSignedScan ? (
        <Alert severity="warning" data-testid="after-signed-scan-start-warning">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {START_BEFORE_SIGNED_SCAN_WARNING}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onResolveStartBeforeSignedScan('full_history')}
            >
              Keep as full Safety Case history
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => onResolveStartBeforeSignedScan('reset_start')}
            >
              Reset start to latest Signed Safety Scan
            </Button>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
};
